import { useMemo, useState, useRef } from 'react'
import { useGQLFetcher } from '../../../context/GQLClientProvider'
import { graphql, useFragment } from '../../../gql'
import { Order, OrderGroupDetailQuery, SubmitOrderMutationMutation } from '../../../gql/graphql'
import { ItemOfEntityResponse } from '../../../lib/RequestUtils'
import {
  areAllOrdersHasSameStatus,
  getNextProcessStatus,
  PROCESS_STATUS_NAME,
  allProcessStatuses,
  printTicket,
  checkTicketError,
} from '../../../lib/Util'
import { useOrderGroupDetailContext } from '../context/OrderGroupDetailContext'
import { GroupOrders_OrderGroupFragment } from '../GroupOrdersTables'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toastSuccess, toastError } from '../../../lib/toastHelpers'
import { useCore } from '../../../hooks'

const SubmitAllOrders_OrderGroupFragment = graphql(`
  fragment SubmitAllOrders_OrderGroup on OrderGroup {
    id
    orders {
      id
      processStatus {
        id
      }
      items {
        id
        clearanceItems {
          id
        }
      }
      packages {
        id
        items {
          id
        }
      }
      carrier {
        id
      }
    }
  }
`)

const SubmitOrderMutation = graphql(`
  mutation SubmitOrderMutation($id: Int!, $status: String!) {
    updateOrder(id: $id, processStatusRelation: { status: $status }) {
      data {
        processStatus {
          id
        }
      }
      errors
    }
  }
`)

const CreatePackageForGroupOrderMutation = graphql(`
  mutation CreatePackageForGroupOrderMutation($order: Int!, $items: [Int!]!, $carrier: String!) {
    createPackage(order: $order, items: $items, carrier: $carrier) {
      errors
      data {
        id
      }
    }
  }
`)

const SendPackageForGroupOrderMutation = graphql(`
  mutation SendPackageForGroupOrderMutation($id: Int!) {
    sendPackage(id: $id) {
      errors
      data {
        id
        ticketUrl
        carrier {
          id
        }
      }
    }
  }
`)

export interface SubmitGroupProgress {
  current: number
  total: number
  errors: Order['id'][]
}

export function getStateForOrderIndex(
  currentProgress: SubmitGroupProgress | null,
  index: number,
  orderId: Order['id'],
): 'success' | 'error' | 'pending' | 'in-progress' | 'excluded' {
  if (!currentProgress) {
    return 'pending'
  }

  if (currentProgress.total < index + 1) {
    return 'excluded'
  }

  if (currentProgress.errors.includes(orderId)) {
    return 'error'
  }

  if (currentProgress.current === index) {
    return 'in-progress'
  }

  if (currentProgress.current > index) {
    return 'success'
  }

  return 'pending'
}

export function useSubmitAllOrders({ orderGroup }: { orderGroup: ItemOfEntityResponse<OrderGroupDetailQuery> | null }) {
  const submitOrder = useGQLFetcher(SubmitOrderMutation)
  const createPackage = useGQLFetcher(CreatePackageForGroupOrderMutation)
  const sendPackage = useGQLFetcher(SendPackageForGroupOrderMutation)

  const group = useFragment(SubmitAllOrders_OrderGroupFragment, orderGroup)

  const [currentProgress, setCurrentProgress] = useState<SubmitGroupProgress | null>(null)
  const [errorsByOrderId, setErrorsByOrderId] = useState<Record<Order['id'], string[]>>({})
  const [isStopping, setIsStopping] = useState(false)
  const isStopped = useRef(false)

  const queryClient = useQueryClient()
  const { core } = useCore()

  const allOrdersHasSameStatus = areAllOrdersHasSameStatus(group?.orders)

  const processStatus = useMemo(() => {
    if (allOrdersHasSameStatus) {
      return group?.orders?.[0]?.processStatus?.id
    } else {
      // Find "lowest" process status (using allProcessStatuses) in "pipeline" and output it
      const lowestStatus = group?.orders?.reduce((lowest, order) => {
        const currentIndex = allProcessStatuses.indexOf(order.processStatus?.id as any)
        const lowestIndex = allProcessStatuses.indexOf(lowest as any)
        return currentIndex < lowestIndex ? order.processStatus?.id : lowest
      }, group?.orders?.[0]?.processStatus?.id)

      return lowestStatus as any
    }
  }, [group])

  // Sort orders so that orders with lowest process status are first
  const sortOrdersByProcessStatus = (orders: Order[]) => {
    return group?.orders?.sort((a, b) => {
      const aIndex = allProcessStatuses.indexOf(a.processStatus?.id as any)
      const bIndex = allProcessStatuses.indexOf(b.processStatus?.id as any)
      return aIndex - bIndex
    })
  }

  const stop = () => {
    isStopped.current = true
    setIsStopping(true)
  }

  const {
    mutate: submitAllOrders,
    isPending,
    error,
  } = useMutation({
    mutationFn: async () => {
      isStopped.current = false
      setIsStopping(false)

      // If the current process status is packed or beyond, do not process further
      if (
        processStatus === 'packed' ||
        processStatus === 'shipped' ||
        processStatus === 'delivered' ||
        processStatus === 'returned' ||
        processStatus === 'cancelled'
      ) {
        return []
      }

      // Sorts the orders and exclides orders that are already moved to the next status
      const orders = sortOrdersByProcessStatus(group?.orders || []).filter(
        order => order.processStatus?.id === processStatus,
      )

      let errorsHappened = false

      setCurrentProgress({
        current: 0,
        total: orders?.length || 0,
        errors: [],
      })

      let nextProcessStatus = getNextProcessStatus(processStatus)
      if (processStatus === 'picking') {
        nextProcessStatus = 'packed'
      }

      const results: SubmitOrderMutationMutation[] = []

      for (let i = 0; i < (orders?.length || 0); i++) {
        if (isStopped.current) {
          break
        }

        const order = orders?.[i]
        let result: SubmitOrderMutationMutation | null = null
        let currentOrderStatus = order.processStatus?.id

        try {
          // Actual order processing happens here:

          if (currentOrderStatus === 'picking') {
            const pickRes = await submitOrder({ id: order.id, status: 'packing' })
            if (pickRes.updateOrder?.errors?.length) {
              result = pickRes
              throw new Error(pickRes.updateOrder?.errors?.join(', '))
            }
            currentOrderStatus = 'packing'
          }

          if (currentOrderStatus === 'packing') {
            // 1. Create packages and pack all order items in them

            if (!order.carrier?.id) {
              throw new Error('Dopravce není vybrán pro tuto objednávku')
            }

            let packageIds: number[] = []

            // If the order already has packages, we need to check if all items are in packages and process them.
            if (order.packages?.length > 0) {
              const itemsInPackages = order.packages.map(p => p.items).flat()
              const itemsNotInPackages = order.items.filter(i => !itemsInPackages.map(p => p.id).includes(i.id))

              if (itemsNotInPackages.length > 0) {
                throw new Error(
                  'Tato objednávka již má vytvořené balíky, ale některé položky nejsou v balících. Zkontrolujte prosím balíky a položky ručně.',
                )
              }

              packageIds = order.packages.map(p => p.id)
            } else {
              // If the order doesn't have packages, we need to create them.
              const packageResult = await createPackage({
                order: order.id,
                items: order.items.map(o => o.id),
                carrier: order.carrier?.id,
              })

              packageIds = [packageResult.createPackage.data.id]
            }

            // 2. Send packages to the carrier and print label
            for (const packageId of packageIds) {
              const sendPackageResult = await sendPackage({ id: packageId })
              const data = sendPackageResult.sendPackage.data
              if (!data?.ticketUrl) {
                throw new Error('Chybí odkaz na štítek (ticketUrl)')
              }
              const hasError = await checkTicketError(data.ticketUrl, core)
              if (hasError) {
                throw new Error('Chyba při stahování tiskových dat štítku. Ověřte správnost nastavení API dopravce')
              }
              await printTicket(
                {
                  ticketUrl: data.ticketUrl,
                  carrier: { id: data.carrier?.id || '' },
                },
                core,
              )
            }
          }
          // 3. Update order status to the next status
          result = await submitOrder({ id: order.id, status: nextProcessStatus })

          // End of actual order processing
        } catch (error) {
          errorsHappened = true
          setCurrentProgress(p => ({
            current: i + 1,
            total: orders?.length || 0,
            errors: [...(p?.errors || []), order?.id],
          }))
          setErrorsByOrderId(e => ({
            ...e,
            [order.id]: error?.message || 'Neznámá chyba',
          }))
          continue
        }

        if (result) {
          results.push(result)
        }

        if (result && Array.isArray(result.updateOrder?.errors) && result.updateOrder?.errors.length > 0) {
          errorsHappened = true
          setCurrentProgress(p => ({
            current: i + 1,
            total: orders?.length || 0,
            errors: [...(p?.errors || []), order?.id],
          }))
          setErrorsByOrderId(e => ({
            ...e,
            [order.id]: result.updateOrder?.errors,
          }))
          continue
        } else {
          setCurrentProgress(p => ({
            current: i + 1,
            total: orders?.length || 0,
            errors: p?.errors || [],
          }))
        }
      }

      queryClient.invalidateQueries({ queryKey: ['orderGroup', { id: group?.id }] })

      if (errorsHappened) {
        throw new Error('Některé objednávky nebyly odeslány')
      }

      if (results.length === 0) {
        return []
      }

      if (isStopped.current) {
        toastSuccess('Zpracování bylo zastaveno. Úspěšně zpracováno: ' + results.length)
        return results
      }

      const areAllSameStatus = results.every(
        result => result.updateOrder?.data?.processStatus?.id === nextProcessStatus,
      )

      if (!areAllSameStatus) {
        throw new Error('Některé objednávky nebyly odeslány')
      }

      toastSuccess('Všechny objednávky byly premisteny do stavu ' + PROCESS_STATUS_NAME[nextProcessStatus])

      if (results?.some(result => Array.isArray(result.updateOrder?.errors) && result.updateOrder?.errors.length > 0)) {
        throw results
          .filter(result => Array.isArray(result.updateOrder?.errors) && result.updateOrder?.errors.length > 0)
          .map(result => result.updateOrder?.errors)
      }

      return results
    },
    onError: () => {
      toastError('Některé objednávky nebyly odeslány')
    },
    onSettled: () => {
      setIsStopping(false)
    },
  })

  return {
    submitAllOrders,
    stop,
    isStopping,
    allOrdersHasSameStatus,
    processStatus: processStatus,
    currentProgress,
    isPending,
    sortOrdersByProcessStatus,
    error,
    errorsByOrderId,
  }
}
